import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/NavBar";
import { useAppDispatch, useAppSelector } from "../store/store";
import AuthAction from "../store/auth/action";
import type { UserContactInfo, UserVerifiedContactInfo } from "../models/auth/auth.model";
import { FaFacebookF, FaInstagram, FaTiktok, FaPhone, FaTimes, FaHourglassHalf } from "react-icons/fa";

const ProfileContactsPage = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { user, isLoading, updateContactSuccess, error } = useAppSelector((s) => s.auth);

    const [contacts, setContacts] = useState<UserContactInfo>({});
    const [inputError, setInputError] = useState("");
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [showPendingModal, setShowPendingModal] = useState(false);

    useEffect(() => {
        if (user?.contact) setContacts({ ...user.contact });
    }, [user?.id]);

    useEffect(() => {
        return () => AuthAction.clearState(dispatch);
    }, [dispatch]);

    const userHasAlreadyPendingContacts = () =>
        !!(
            user?.verifiedContacts?.facebook === "pending" ||
            user?.verifiedContacts?.instagram === "pending" ||
            user?.verifiedContacts?.tiktok === "pending" ||
            user?.verifiedContacts?.phone === "pending"
        );

    const getVerifiedContacts = (): UserVerifiedContactInfo => ({
        facebook: !contacts.facebook ? "" : contacts.facebook !== user?.contact?.facebook ? "pending" : (user?.verifiedContacts?.facebook ?? ""),
        instagram: !contacts.instagram ? "" : contacts.instagram !== user?.contact?.instagram ? "pending" : (user?.verifiedContacts?.instagram ?? ""),
        tiktok: !contacts.tiktok ? "" : contacts.tiktok !== user?.contact?.tiktok ? "pending" : (user?.verifiedContacts?.tiktok ?? ""),
        phone: !contacts.phone ? "" : contacts.phone !== user?.contact?.phone ? "pending" : (user?.verifiedContacts?.phone ?? ""),
    });

    const validateInputs = () =>
        !!(contacts.instagram || contacts.facebook || contacts.tiktok || contacts.phone);

    const areContactsEdited = () =>
        contacts.facebook !== user?.contact?.facebook ||
        contacts.instagram !== user?.contact?.instagram ||
        contacts.tiktok !== user?.contact?.tiktok ||
        contacts.phone !== user?.contact?.phone;

    const noApprovedContactsWillRemain = () => {
        const v = getVerifiedContacts();
        return v.facebook !== "approved" && v.instagram !== "approved" && v.tiktok !== "approved" && v.phone !== "approved";
    };

    const onConfirm = () => {
        setInputError("");
        if (userHasAlreadyPendingContacts()) {
            setInputError("Ju tashmë keni së paku një nga kontaktet në pritje për aprovim");
            return;
        }
        if (!areContactsEdited()) {
            setInputError("Ju nuk keni ndryshuar të dhënat");
            return;
        }
        if (!validateInputs()) {
            setInputError("Ju lutemi jepni linkun e së paku njërës nga rrjetet sociale ose numrin e telefonit");
            return;
        }
        if (noApprovedContactsWillRemain()) {
            setShowWarningModal(true);
            return;
        }
        AuthAction.updateUserContacts(user!, contacts, getVerifiedContacts(), dispatch);
    };

    const onConfirmWarning = () => {
        setShowWarningModal(false);
        AuthAction.updateUserContacts(user!, contacts, getVerifiedContacts(), dispatch, "pending");
    };

    const handleClear = (field: keyof UserContactInfo) => {
        const status = user?.verifiedContacts?.[field];
        if (status === "pending") {
            setShowPendingModal(true);
            return;
        }
        setContacts((prev) => ({ ...prev, [field]: "" }));
    };

    const displayError = inputError || error;
    const contactRow = (
        field: keyof UserContactInfo,
        Icon: React.ComponentType<{ className?: string }>,
        placeholder: string,
        inputType: "url" | "tel" = "url"
    ) => {
        const value = contacts[field] ?? "";
        const status = user?.verifiedContacts?.[field];
        const isPending = status === "pending";
        const isRejected = status === "rejected";
        return (
            <div
                className={`rounded-xl border-2 p-4 ${isRejected ? "border-red-300 bg-red-50" : "border-gray-200 bg-white"}`}
            >
                <div className="flex items-center gap-3">
                    <Icon className="text-[#704e37] text-xl shrink-0" />
                    <input
                        type={inputType}
                        value={value}
                        onChange={(e) => setContacts((prev) => ({ ...prev, [field]: e.target.value }))}
                        placeholder={placeholder}
                        disabled={isPending}
                        className="flex-1 min-w-0 border-0 bg-transparent p-0 text-gray-800 placeholder-gray-400 focus:ring-0 disabled:opacity-70"
                    />
                    {value ? (
                        <button
                            type="button"
                            onClick={() => handleClear(field)}
                            className="shrink-0 p-1 text-[#704e37] hover:opacity-80"
                            aria-label="Hiq"
                        >
                            {isPending ? <FaHourglassHalf className="text-amber-600" /> : <FaTimes />}
                        </button>
                    ) : null}
                </div>
                {isRejected && <p className="text-red-600 text-sm mt-1">Refuzuar</p>}
                {isPending && <p className="text-amber-600 text-sm mt-1">Në pritje</p>}
            </div>
        );
    };

    if (!user) {
        return (
            <div className="min-h-screen bg-[#f8f2ed] font-manrope">
                <Navbar />
                <div className="max-w-xl mx-auto px-4 py-6">Duke u ngarkuar...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#f8f2ed] font-manrope">
            <Navbar />
            <div className="max-w-xl mx-auto px-4 py-6">
                <Link to="/profile" className="text-[#704e37] text-sm font-medium mb-4 inline-block">← Prapa në profilin</Link>
                <h1 className="text-2xl font-semibold text-[#704e37] mb-4">Kontaktet</h1>

                <div className="bg-white rounded-xl shadow p-6 space-y-4">
                    <p className="text-gray-700 font-medium">Rrjetet sociale dhe numri i telefonit</p>
                    <p className="text-gray-600 text-sm">
                        Ju duhet të jepni së paku kontaktin e njërit nga rrjetet sociale të më poshtme apo numrin e telefonit për të verifikuar llogarinë tuaj. Ju do të kontaktoheni nga ne përmes njërit nga rrjetet sociale që ju i jepni më poshtë.
                    </p>

                    <div>
                        {contactRow("phone", FaPhone, "Numri i telefonit (+383...)", "tel")}
                        <p className="text-xs text-gray-500 mt-0.5 ml-1">Numri i telefonit duhet të fillojë me +</p>
                    </div>
                    {contactRow("facebook", FaFacebookF, "Linku i profilit të Facebook")}
                    {contactRow("instagram", FaInstagram, "Linku i profilit të Instagram")}
                    {contactRow("tiktok", FaTiktok, "Linku i profilit të TikTok")}

                    {displayError && (
                        <p className="text-red-600 text-sm flex items-center gap-1">
                            <FaTimes className="shrink-0" /> {displayError}
                        </p>
                    )}

                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={isLoading}
                        className="w-full py-3 rounded-lg bg-[#704e37] text-white font-medium hover:opacity-90 disabled:opacity-50 mt-4"
                    >
                        {isLoading ? "Duke ruajtur..." : "Konfirmo"}
                    </button>
                </div>
            </div>

            {updateContactSuccess && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
                    <div className="bg-white rounded-xl shadow-lg max-w-sm w-full p-6 text-center">
                        <p className="font-medium text-gray-800 mb-1">Sukses</p>
                        <p className="text-gray-600 text-sm mb-4">Kontaktet u përditësuan me sukses. Ato janë në pritje për t'u aprovuar.</p>
                        <button
                            onClick={() => {
                                AuthAction.clearState(dispatch);
                                navigate("/profile");
                            }}
                            className="w-full py-2.5 rounded-lg bg-[#704e37] text-white font-medium"
                        >
                            Mbyll
                        </button>
                    </div>
                </div>
            )}

            {/* Warning: no approved contacts will remain */}
            {showWarningModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowWarningModal(false)}>
                    <div className="bg-white rounded-xl shadow-lg max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
                        <p className="font-medium text-gray-800 mb-1">Vëmendje</p>
                        <p className="text-gray-600 text-sm mb-4 whitespace-pre-line">
                            Ju do mbeteni pa asnjë kontakt të aprovuar deri në aprovimin e radhës. Kjo do të thotë që ju nuk do të mund të postoni apo ndryshoni shpalljet tuaja deri kur kontakti juaj aprovohet.
                            {"\n\n"}Nëse dëshironi të vazhdoni me ndryshimin e kontaktit, shtypni &quot;Konfirmo&quot;.
                        </p>
                        <div className="flex gap-2">
                            <button onClick={() => setShowWarningModal(false)} className="flex-1 py-2.5 rounded-lg border border-gray-300 font-medium">Anulo</button>
                            <button onClick={onConfirmWarning} className="flex-1 py-2.5 rounded-lg bg-[#704e37] text-white font-medium">Konfirmo</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Pending: can't clear while pending */}
            {showPendingModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowPendingModal(false)}>
                    <div className="bg-white rounded-xl shadow-lg max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
                        <p className="font-medium text-gray-800 mb-1">Në pritje</p>
                        <p className="text-gray-600 text-sm mb-4">Statusi i këtij rrjeti social është ende në pritje.</p>
                        <button onClick={() => setShowPendingModal(false)} className="w-full py-2.5 rounded-lg bg-[#704e37] text-white font-medium">Mbyll</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfileContactsPage;
